import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import BlogCard from '../components/BlogCard';
import Pagination from '../components/Pagination';
import { supabase } from '../supabaseClient';
import { calculateReadingTime } from '../utils/blog';
import { DEFAULT_BLOG_DESCRIPTION, DEFAULT_OG_IMAGE, getCanonicalUrl } from '../utils/seo';
import { useBranding } from '../context/BrandingContext';

const PAGE_SIZE = 6;

const BlogPage = ({ onNavigate }) => {
  const { branding } = useBranding();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  const catMap = useMemo(() => {
    const map = new Map();
    categories.forEach((cat) => {
      if (cat?.id != null) map.set(cat.id, cat);
    });
    return map;
  }, [categories]);

  const go = (path) => {
    if (typeof onNavigate === 'function') {
      onNavigate(path);
    } else {
      window.location.assign(path);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error: catsError } = await supabase
        .from('categories')
        .select('id, name, slug, is_active, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (catsError) {
        console.error('Error cargando categorías de blog:', catsError);
        return;
      }

      setCategories(data || []);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        const from = (currentPage - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        let query = supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, content, cover_image_url, published_at, category_id', {
            count: 'exact',
          })
          .eq('is_published', true)
          .order('published_at', { ascending: false, nullsFirst: false });

        if (selectedCategory !== 'all') {
          query = query.eq('category_id', selectedCategory);
        }

        const { data, error: postsError, count } = await query.range(from, to);

        if (postsError) {
          throw postsError;
        }

        const normalized = (data || []).map((post) => ({
          ...post,
          readingTime: calculateReadingTime(post.content || post.excerpt || ''),
        }));

        setPosts(normalized);
        setTotalPosts(count || 0);
      } catch (err) {
        console.error('Error cargando posts:', err);
        setPosts([]);
        setTotalPosts(0);
        setError('Tuvimos un problema al cargar el blog. Intenta más tarde.');
      } finally {
        setLoading(false);
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };

    fetchPosts();
  }, [selectedCategory, currentPage]);

  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const renderPosts = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="rounded-3xl border border-gray-100 bg-white shadow animate-pulse">
              <div className="aspect-[4/3] bg-gray-200 rounded-t-3xl" />
              <div className="p-6 space-y-4">
                <div className="h-3 bg-gray-200 rounded-full w-20" />
                <div className="h-6 bg-gray-200 rounded-full w-3/4" />
                <div className="h-4 bg-gray-200 rounded-full w-full" />
                <div className="h-4 bg-gray-200 rounded-full w-5/6" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-100 bg-red-50 text-red-700 px-6 py-4">
          {error}
        </div>
      );
    }

    if (!posts.length) {
      return (
        <div className="rounded-2xl border border-gray-100 bg-white px-8 py-12 text-center text-gray-500">
          No encontramos entradas para esta categoría todavía.
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <BlogCard
              key={post.id}
              post={post}
              category={catMap.get(post.category_id)}
              readingTime={post.readingTime}
              onClick={() => go(`/blog/${post.slug}`)}
            />
          ))}
        </div>
        {totalPosts > PAGE_SIZE && (
          <Pagination
            productsPerPage={PAGE_SIZE}
            totalProducts={totalPosts}
            paginate={setCurrentPage}
            currentPage={currentPage}
          />
        )}
      </>
    );
  };

  const categoryButtons = [
    { id: 'all', label: 'Todas las categorías' },
    ...categories.map((cat) => ({
      id: cat.id,
      label: cat.name,
    })),
  ];
  const categorySentence = useMemo(() => {
    const names = categories
      .map((cat) => (cat?.name || '').trim())
      .filter(Boolean);

    if (!names.length) {
      return 'las categorías que más nos inspiran';
    }

    const lower = names.map((name) => name.toLowerCase());
    if (lower.length === 1) return lower[0];
    if (lower.length === 2) return `${lower[0]} y ${lower[1]}`;
    return `${lower.slice(0, -1).join(', ')} y ${lower[lower.length - 1]}`;
  }, [categories]);
  const canonicalUrl = getCanonicalUrl('/blog');
  const pageTitle = 'Blog | El Ancla CR';
  const description = DEFAULT_BLOG_DESCRIPTION;
  const ogImage = branding.meta.ogImage || DEFAULT_OG_IMAGE;

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <section className="bg-gradient-to-br from-[#EEF2FF] via-white to-[#FDF2F8] py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-sm uppercase tracking-[0.4em] text-brand font-semibold mb-4">
            Blog
          </p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 max-w-3xl leading-tight mb-6">
            Inspírate, aprende y descubre las historias detrás de cada producto
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            Seleccionamos las mejores tendencias en {categorySentence} para ayudarte
            a tomar decisiones informadas.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-wrap gap-4 mb-10">
            {categoryButtons.map((cat) => {
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleSelectCategory(cat.id)}
                  className={`px-5 py-3 rounded-2xl border text-sm font-semibold transition-all duration-200 shadow-sm
                    ${isActive
                      ? 'bg-brand text-white border-brand'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                    }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {renderPosts()}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
